import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, LogIn, ArrowRight } from "lucide-react";
import { loginStudent } from "../../api/student/studentAuthApi";
import {
  Button,
  Input,
  Card,
  CardContent,
  Alert,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const StudentLogin = () => {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [warning, setWarning] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toast = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWarning("");

    if (!username || !password) {
      toast.error("Please enter both Student ID and password");
      return;
    }

    setSubmitting(true);

    try {
      const data = await loginStudent(username, password);
      const studentData = data?.student || data;

      if (studentData?.passwordChanged === false) {
        setWarning(
          "You are using a temporary password. Please change it in your profile settings."
        );
      }

      navigate("/student-home");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          "Login failed. Please check your credentials."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-neutral-50 dark:bg-neutral-950">
      {/* Logo */}
      <div className="px-5 py-5 sm:px-8 sm:py-6">
        <Link to="/" className="inline-flex items-center">
          <img
            src="https://res.cloudinary.com/hsvfv0h8/image/upload/v1786152037/Untitled_design__6_-removebg-preview_zijzg4.png"
            alt="EduFlow"
            className="h-11 w-auto object-contain sm:h-12"
          />
        </Link>
      </div>

      {/* Authentication */}
      <main className="flex justify-center px-5 pb-10 pt-5 sm:px-6 sm:pt-7">
        <div className="w-full max-w-[400px]">
          <Card className="rounded-xl border border-neutral-200 bg-white shadow-none dark:border-neutral-800 dark:bg-neutral-900">
            <CardContent className="p-7 sm:p-8">
              {/* Header */}
              <div className="mb-7">
                <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                  Student Login
                </h1>

                <p className="mt-1.5 text-sm leading-5 text-neutral-500 dark:text-neutral-400">
                  Sign in to access your student portal.
                </p>
              </div>

              {/* Temporary Password Warning */}
              {warning && (
                <Alert
                  variant="warning"
                  className="mb-6 rounded-lg px-3.5 py-3 text-xs leading-5"
                >
                  {warning}
                </Alert>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Student ID */}
                <div className="px-0.5">
                  <Input
                    label="Student ID"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. SCH001-STU-00001"
                    required
                    disabled={submitting}
                    className="h-11 px-3 text-sm"
                  />
                </div>

                {/* Password */}
                <div className="relative px-0.5">
                  <Input
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    disabled={submitting}
                    className="h-11 px-3 pr-10 text-sm"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword((prev) => !prev)
                    }
                    disabled={submitting}
                    aria-label={
                      showPassword
                        ? "Hide password"
                        : "Show password"
                    }
                    className="absolute right-3.5 top-[34px] flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 transition-colors hover:text-neutral-700 focus:outline-none focus:ring-2 focus:ring-neutral-300 disabled:pointer-events-none disabled:opacity-50 dark:text-neutral-500 dark:hover:text-neutral-200 dark:focus:ring-neutral-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>

                {/* Submit */}
                <div className="pt-1">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={submitting}
                    leftIcon={<LogIn className="h-4 w-4" />}
                    className="h-11 text-sm font-medium"
                  >
                    Login
                  </Button>
                </div>
              </form>

              {/* Staff Login */}
              <div className="mt-6 border-t border-neutral-100 dark:border-neutral-800 pt-5 dark:border-neutral-800">
                <div className="flex justify-center">
                  <Link
                    to="/employee-login"
                    className="group inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
                  >
                    <span>Staff Login</span>

                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default StudentLogin;